import {Lifetime, listenWindowEvent, moduleLifetime} from "./Lifetime";
import {stringCmp} from "./SortUtil";

export enum LogLevel {
    TRACE = 0,
    DEBUG = 1,
    INFO = 2,
    WARN = 3,
    ERROR = 4,
    OFF = 5,
}

const LOGGER_LEVELS_KEY = 'loggers';
export const DEFAULT_LOG_LEVEL = LogLevel.WARN;

export type LoggerSnapshot = ReadonlyArray<{ name: string; level: LogLevel }>;

class LoggerRegistry {
    private levels: Map<string, LogLevel> = new Map();
    private snapshot: LoggerSnapshot = [];
    private listeners: Set<() => void> = new Set();

    constructor(lifetime: Lifetime = new Lifetime()) {
        this.loadFromLocalStorage();
        this.notify();


        // Subscribe to localStorage changes from other tabs/windows
        listenWindowEvent(lifetime, 'storage', event => {
            if (event.key === LOGGER_LEVELS_KEY) {
                this.loadFromLocalStorage();
                console.log('Updated logger levels', this.levels);
                this.notify();
            }
        });
    }

    private loadFromLocalStorage(): void {
        try {
            const stored = localStorage.getItem(LOGGER_LEVELS_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                Object.entries(parsed).forEach(([name, level]) => {
                    if (typeof level === 'string' && level in LogLevel) {
                        this.levels.set(name, LogLevel[level as keyof typeof LogLevel]);
                    } else if (typeof level === 'number') {
                        this.levels.set(name, level as LogLevel);
                    }
                });
            }
        } catch (e) {
            console.error('Failed to load logger levels from localStorage:', e);
        }
    }

    private saveToLocalStorage(): void {
        const levels: any = {};
        this.levels.forEach((level, name) => {
            levels[name] = LogLevel[level];
        });
        localStorage.setItem(LOGGER_LEVELS_KEY, JSON.stringify(levels));
    }

    private notify(): void {
        this.snapshot = Array.from(this.levels.entries())
            .map(([name, level]) => ({name, level}))
            .sort(stringCmp('name'));
        this.listeners.forEach(listener => listener());
    }

    getLevel(name: string): LogLevel {
        return this.levels.get(name) ?? DEFAULT_LOG_LEVEL;
    }

    getLevels(): ReadonlyMap<string, LogLevel> {
        return this.levels;
    }

    getSnapshot(): LoggerSnapshot {
        return this.snapshot;
    }

    setLevel(name: string, level: LogLevel): void {
        this.levels.set(name, level);
        this.saveToLocalStorage();
        this.notify();
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    register(name: string): void {
        if (!this.levels.has(name)) {
            this.levels.set(name, DEFAULT_LOG_LEVEL);
            this.saveToLocalStorage();
            this.notify();
        }
    }
}

export const loggerRegistry = new LoggerRegistry(moduleLifetime(module));

interface LoggerApi {
    (...data: any[]): void;
    log(...data: any[]): void;
    trace(...data: any[]): void;
    debug(...data: any[]): void;
    info(...data: any[]): void;
    warn(...data: any[]): void;
    error(...data: any[]): void;
    isEnabled(level: LogLevel): boolean;
}


function formatLoggerName(name: string, withId: boolean, targetLength: number = 30): string {
    const idSuffix = withId ? `[${Math.random().toString(36).substring(2, 10)}]` : '';

    const fullName = `${name}${idSuffix}`;

    if (fullName.length > targetLength) {
        // Cut from the name, preserve idSuffix
        const availableLength = targetLength - idSuffix.length;
        return name.substring(0, availableLength) + idSuffix;
    } else if (fullName.length < targetLength) {
        // Prepend with spaces
        return ' '.repeat(targetLength - fullName.length) + fullName;
    }
    return fullName;
}

export function Logger(name: string, {withId = false}: {
    withId?: boolean,
} = {}): LoggerApi {
    loggerRegistry.register(name);

    const formattedName = formatLoggerName(name, withId);
    const isEnabled = (level: LogLevel) => level >= loggerRegistry.getLevel(name);
    const messageFn = (fn: (...data: any[]) => void, messageLevel: LogLevel): ((...data: any[]) => void) => {
        return (...data) => {
            if (isEnabled(messageLevel)) {
                fn(`${formattedName}:`, ...data);
            }
        }
    };

    const logFn = messageFn(console.log, LogLevel.INFO);
    const logger = ((...data: any[]) => logFn(...data)) as LoggerApi;

    logger.log = logFn;
    logger.trace = messageFn(console.trace, LogLevel.TRACE);
    logger.debug = messageFn(console.debug, LogLevel.DEBUG);
    logger.info = messageFn(console.info, LogLevel.INFO);
    logger.warn = messageFn(console.warn, LogLevel.WARN);
    logger.error = messageFn(console.error, LogLevel.ERROR);
    logger.isEnabled = isEnabled;

    return logger;
}
